import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from '../../redux/store';
import { useMsal } from "@azure/msal-react";
import {
  getResourcingForecast,
  getResourcingTeams,
  getResourcingSummary,
  getResourcingBreakdown,
  clearError,
  clearUpdateMessage,
  setFilters
} from '../../redux/slices/resourcing';

import styles from './Resourcing.module.css';
import ResourcingFilters from '../../Components/ResourceCalander/ResourcingFilters';
import ResourcingTable from '../../Components/ResourceCalander/ResourcingTable';
import ApiResponseDisplay from '../../Components/ApiResponseDisplay/ApiResponseDisplay';
import { CircularProgress } from "@mui/material";
import buttonStyles from '../../GlobalStyles/button.module.css';

function Resourcing() {
  const dispatch = useDispatch();
  const { accounts } = useMsal();
  const account = accounts[0];

  const {
    forecast,
    breakdown,
    summary,
    isLoading,
    isSummaryLoading,
    isBreakdownLoading,
    error,
    updateMessage,
    filters
  } = useSelector((state) => state.resourcing);

  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Initialize data on component mount
  useEffect(() => {
    dispatch(getResourcingTeams());
  }, [dispatch]);

  // Fetch data when filters change
  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      dispatch(getResourcingBreakdown({
        startDate: filters.startDate,
        endDate: filters.endDate,
        teamFilter: filters.teamFilter
      }));

      dispatch(getResourcingSummary({
        startDate: filters.startDate,
        endDate: filters.endDate,
        teamFilter: filters.teamFilter
      }));
    }
  }, [dispatch, filters]);

  // Handle error display
  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  // Handle success message display
  useEffect(() => {
    if (updateMessage) {
      setShowSuccess(true);
    }
  }, [updateMessage]);

  const handleFiltersChange = (newFilters) => {
    // The filters are already updated in the ResourcingFilters component
    // This is mainly for any additional side effects if needed
    console.log('Filters changed:', newFilters);
  };

  const handleReset = () => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);

    const endDate = new Date(monday);
    endDate.setDate(monday.getDate() + (4 * 7) - 1);

    const resetFilters = {
      startDate: monday.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      teamFilter: 'NZ'
    };

    dispatch(setFilters(resetFilters));
  };

  const handleCloseError = () => {
    setShowError(false);
    dispatch(clearError());
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    dispatch(clearUpdateMessage());
  };

  return (
    <div className={styles.ResourcingMainContent}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderContent}>
          <div className={styles.pageHeaderText}>
            <h1 className={styles.pageTitle}>Resourcing Forecast</h1>
            <p className={styles.pageSubtitle}>
              View team member allocation from Microsoft Planner data
            </p>
          </div>
          <button
            className={`${buttonStyles.Button} ${styles.resetFiltersButton}`}
            onClick={handleReset}
          >
            Refresh Data
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {!isSummaryLoading && summary && (
        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardContent}>
              <div className={styles.summaryCardNumber}>
                {(() => {
                  // Prefer accurate headcount from breakdown (excluding hidden names)
                  const excludedNames = ['David Coles', 'Christina Carlsson'];
                  if (breakdown && breakdown.individual_rows && Array.isArray(breakdown.individual_rows)) {
                    const count = breakdown.individual_rows.filter(row => {
                      const name = row[0] || '';
                      return !excludedNames.includes(name);
                    }).length;
                    return count;
                  }
                  return summary.total_people;
                })()}
              </div>
              <div className={styles.summaryCardLabel}>
                Team Members
              </div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardContent}>
              <div className={styles.summaryCardNumber}>
                {summary.total_scheduled_hours}h
              </div>
              <div className={styles.summaryCardLabel}>
                Total Scheduled
              </div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardContent}>
              <div className={styles.summaryCardNumber}>
                {summary.total_unscheduled_hours || 0}h
              </div>
              <div className={styles.summaryCardLabel}>
                Unscheduled Hours
              </div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardContent}>
              <div className={styles.summaryCardNumber}>
                {summary.total_construction_queries || 0}h
              </div>
              <div className={styles.summaryCardLabel}>
                Construction Queries
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Section moved inline with table title below when compact */}

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Inline, non-blocking loader */}
        {isBreakdownLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px' }}>
            <CircularProgress size={40} />
          </div>
        )}

        {/* Forecast Table */}
        <div className={styles.tableSection}>
          <div className={styles.tableTitleRow}>
            <h2 className={styles.tableTitle}>Weekly Allocation Forecast</h2>
            <ResourcingFilters compact={true} onFiltersChange={handleFiltersChange} />
          </div>
          
          <ResourcingTable
            data={breakdown}
            isLoading={isBreakdownLoading}
            error={error}
          />
        </div>

      </div>

      {/* Error Notification */}
      {showError && (
        <div className={styles.errorNotification}>
          <div className={styles.errorContent}>
            <span className={styles.errorText}>
              {error || 'An error occurred while loading resourcing data'}
            </span>
            <button 
              className={styles.errorCloseButton}
              onClick={handleCloseError}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {showSuccess && (
        <div className={styles.successNotification}>
          <div className={styles.successContent}>
            <span className={styles.successText}>
              Resource allocation updated successfully!
            </span>
            <button 
              className={styles.successCloseButton}
              onClick={handleCloseSuccess}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* API Response Display (if needed) */}
      {updateMessage && (
        <ApiResponseDisplay
          response={updateMessage}
          onClose={() => dispatch(clearUpdateMessage())}
        />
      )}
    </div>
  );
}

export default Resourcing; 